import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  Users, Activity, MessageSquareHeart, Crown, Flame, Mail,
  TrendingUp, GraduationCap, Bell, DollarSign,
} from "lucide-react";

const ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || "demo@test.com")
  .split(",").map((e) => e.trim().toLowerCase());

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics")
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load"))
      .finally(() => setLoading(false));
  }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (!ADMIN_EMAILS.includes((user.email || "").toLowerCase())) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: "Outfit" }}>Admin access required</h1>
        <Link to="/app" className="text-indigo-600 font-bold">Back to dashboard</Link>
      </div>
    );
  }
  if (loading) return <p className="text-slate-500">Loading metrics…</p>;
  if (error) return <p className="text-rose-600">{error}</p>;
  if (!data) return null;

  return (
    <div className="space-y-8" data-testid="admin-analytics-page">
      <header>
        <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Launch Analytics</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Live metrics across users, activity, acquisition, and revenue.</p>
      </header>

      {/* USERS */}
      <Section title="Users">
        <Card icon={Users} color="indigo" label="Total users" value={data.users.total} testId="metric-total-users" />
        <Card icon={TrendingUp} color="emerald" label="New (7 days)" value={data.users.new_7d} testId="metric-new-7d" />
        <Card icon={GraduationCap} color="rose" label="Teachers" value={data.users.teachers} testId="metric-teachers" />
        <Card icon={Users} color="amber" label="Parents" value={data.users.parents} testId="metric-parents" />
        <Card icon={Users} color="sky" label="Students" value={data.users.students} testId="metric-students" />
      </Section>

      {/* ACTIVITY */}
      <Section title="Activity">
        <Card icon={Flame} color="orange" label="DAU" value={data.activity.dau} testId="metric-dau" />
        <Card icon={Activity} color="indigo" label="WAU" value={data.activity.wau} testId="metric-wau" />
        <Card icon={Activity} color="violet" label="Writing submissions" value={data.activity.writing_submissions} testId="metric-submissions" />
        <Card icon={Activity} color="rose" label="AI feedback" value={data.activity.ai_feedback_generated} testId="metric-ai-feedback" />
        <Card icon={Activity} color="emerald" label="Revision sessions" value={data.activity.revision_sessions} testId="metric-revisions" />
        <Card icon={GraduationCap} color="indigo" label="Teacher dashboard views" value={data.activity.teacher_dashboard_views} testId="metric-tdash" />
      </Section>

      {/* ACQUISITION */}
      <Section title="Acquisition & Engagement">
        <Card icon={Mail} color="rose" label="Teacher pilot requests" value={data.acquisition.teacher_pilot_requests} testId="metric-pilots" />
        <Card icon={MessageSquareHeart} color="violet" label="Feedback responses" value={data.acquisition.feedback_count} testId="metric-feedback" />
        <Card icon={Bell} color="amber" label="Waitlist signups" value={data.acquisition.waitlist_signups} testId="metric-waitlist" />
      </Section>

      {/* REVENUE */}
      <Section title="Revenue">
        <Card icon={Crown} color="amber" label="Founder members" value={data.revenue.founder_members} testId="metric-founders"
          sub={`${data.revenue.founder_seats_remaining} seats left of 327`} />
        <Card icon={Users} color="indigo" label="Student subscribers" value={data.revenue.student_subscribers} testId="metric-subs" />
        <Card icon={DollarSign} color="emerald" label="MRR (USD)" value={`$${(data.revenue.mrr_usd || 0).toLocaleString()}`} testId="metric-mrr" />
      </Section>

      {/* RECENT FEEDBACK */}
      <Section title="Recent feedback" full>
        <div className="col-span-full tactile-card p-6">
          {data.recent_feedback.length === 0 ? (
            <p className="text-sm text-slate-500">No feedback yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.recent_feedback.map((f) => (
                <li key={f.id} className="py-3 flex items-start gap-3" data-testid={`fb-${f.id}`}>
                  <div className="text-amber-500 font-bold">{"★".repeat(f.rating)}<span className="text-slate-300 dark:text-slate-700">{"★".repeat(5 - f.rating)}</span></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-400 dark:text-slate-500">{f.email || "anonymous"} · {f.created_at?.slice(0, 10)}</div>
                    {f.what_worked && <div className="text-sm text-slate-800 dark:text-slate-200 mt-0.5"><span className="font-bold">Worked:</span> {f.what_worked}</div>}
                    {f.what_confusing && <div className="text-sm text-slate-800 dark:text-slate-200"><span className="font-bold">Confusing:</span> {f.what_confusing}</div>}
                    {f.next_feature && <div className="text-sm text-slate-800 dark:text-slate-200"><span className="font-bold">Wants:</span> {f.next_feature}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>

      <Section title="Recent teacher pilots" full>
        <div className="col-span-full tactile-card p-6">
          {data.recent_pilots.length === 0 ? (
            <p className="text-sm text-slate-500">No pilot applications yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.recent_pilots.map((p) => (
                <li key={p.id} className="py-3 flex items-center justify-between gap-3" data-testid={`pilot-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 dark:text-slate-100">{p.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{p.school} · {p.email} · {p.grade_level} · {p.interest_level}</div>
                  </div>
                  <span className="text-xs text-slate-400">{p.created_at?.slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>
    </div>
  );
}

const Section = ({ title, children, full }) => (
  <section>
    <h2 className="text-xs uppercase font-bold tracking-widest text-slate-500 dark:text-slate-400 mb-3">{title}</h2>
    <div className={`grid ${full ? "grid-cols-1" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"} gap-3`}>{children}</div>
  </section>
);

const Card = ({ icon: Icon, color, label, value, sub, testId }) => (
  <div data-testid={testId} className="tactile-card p-4">
    <Icon size={16} className={`text-${color}-600 dark:text-${color}-400 mb-2`} />
    <div className="text-3xl font-bold text-slate-900 dark:text-slate-50" style={{ fontFamily: "Outfit" }}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400 mt-1">{label}</div>
    {sub && <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">{sub}</div>}
  </div>
);
